/**
 * Sheet templates on the client (ADR-0013).
 *
 * A starter arrives as a *recipe*: cells without ids, prose as i18n keys. Turning one
 * into a document happens here, in the reader's locale, with fresh cell ids -- so two
 * sheets made from one starter share nothing, and a starter's text falls under the
 * same EN/FR parity gate as every other string (14.4).
 *
 * A workspace template arrives as a document and only needs fresh ids.
 *
 * Either way the result is an ordinary `SheetDoc` handed to the ordinary create call.
 * There is no "instantiate" endpoint, on purpose: the sheet that results is the user's
 * own, unambiguously, and carries the same unverified marker as any other
 * (ADR-0013 rule 3).
 */
import { api } from '@/api/client'
import { newCellId } from './model/document'
import type { Cell, SheetDoc, TableRow } from './model/types'
import { emptySheet } from './useSheet'

/* --- what the server sends -------------------------------------------------------- */

export type RecipeCell =
  | { readonly type: 'math'; readonly latex?: string }
  | { readonly type: 'text'; readonly bodyKey: string }
  | { readonly type: 'table'; readonly captionKey?: string; readonly rows?: readonly TableRow[] }
  | {
      readonly type: 'plot'
      readonly captionKey?: string
      readonly latex?: string
      readonly sweep: string
      readonly from: number
      readonly to: number
      readonly steps: number
    }
  | { readonly type: 'image'; readonly alt?: string }

export interface StarterRecipe {
  readonly id: string
  readonly category: string
  readonly title_key: string
  readonly description_key: string
  readonly cell_count: number
  readonly cells: readonly RecipeCell[]
  /**
   * ADR-0016 rule 1: what a curated content template claims each symbol comes to.
   *
   * Read from `starters.json` by the test suite and asserted against this engine —
   * if we ship arithmetic, we test the arithmetic. The API deliberately does not
   * serve it: it is a build-time contract, not something the gallery renders.
   */
  readonly expect?: Readonly<Record<string, { readonly value: number; readonly unit: string }>>
}

export interface WorkspaceTemplateSummary {
  readonly id: number
  readonly title: string
  readonly description: string
  /** A free tag the author typed. Never an i18n key — a firm's taxonomy is its own. */
  readonly category: string | null
  readonly cell_count: number
  readonly engine_version: string
  /** Only ever `false` when the viewer may un-shelve it; others are not shown it. */
  readonly enabled: boolean
  readonly created_by: number
  /** Shown in the gallery. Attribution is what makes moderating a shelf after the
   *  fact workable, which is the trade ADR-0017 made when every member could add. */
  readonly created_by_name: string | null
  readonly created_at: string
  readonly updated_at: string | null
}

export interface WorkspaceTemplateDetail extends WorkspaceTemplateSummary {
  readonly document: SheetDoc
}

export interface TemplateCapabilities {
  /** Save a readable sheet as a workspace template. Every member (ADR-0017). */
  readonly can_create: boolean
  /** Rename, shelve or delete anyone's, and see the shelved ones. */
  readonly can_curate: boolean
}

export interface TemplateCatalogue {
  readonly starters: readonly StarterRecipe[]
  readonly workspace: readonly WorkspaceTemplateSummary[]
  /** The fixed category keys in gallery order, from the file both sides read. */
  readonly categories: readonly string[]
  /** Resolved server-side per request, like `can_edit` on a sheet. */
  readonly capabilities: TemplateCapabilities
}

/** One key for every reader of the catalogue, so a save invalidates the gallery too. */
export const TEMPLATES_QUERY_KEY = ['sheet-templates'] as const

export function listTemplates(signal?: AbortSignal): Promise<TemplateCatalogue> {
  return api.get<TemplateCatalogue>('/sheet-templates', signal)
}

export function getWorkspaceTemplate(
  id: number,
  signal?: AbortSignal,
): Promise<WorkspaceTemplateDetail> {
  return api.get<WorkspaceTemplateDetail>(`/sheet-templates/workspace/${id}`, signal)
}

/** The server copies the sheet it holds, so flush the autosave first. */
export function saveAsTemplate(input: {
  sheet_id: number
  title: string
  description?: string
  category?: string
}): Promise<WorkspaceTemplateDetail> {
  return api.post<WorkspaceTemplateDetail>('/sheet-templates/workspace', input)
}

export function updateWorkspaceTemplate(
  id: number,
  input: { title?: string; description?: string; category?: string; enabled?: boolean },
): Promise<WorkspaceTemplateDetail> {
  return api.patch<WorkspaceTemplateDetail>(`/sheet-templates/workspace/${id}`, input)
}

export function deleteWorkspaceTemplate(id: number): Promise<void> {
  return api.delete<void>(`/sheet-templates/workspace/${id}`)
}

/* --- from recipe to document ------------------------------------------------------ */

export type Translate = (key: string) => string

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** A text cell holds sanitised HTML; a catalogue string is plain prose. */
function paragraph(text: string): string {
  return `<p>${escapeHtml(text)}</p>`
}

function materialiseCell(cell: RecipeCell, t: Translate): Cell {
  const id = newCellId()
  switch (cell.type) {
    case 'math':
      return { id, type: 'math', latex: cell.latex ?? '' }
    case 'text':
      return { id, type: 'text', html: paragraph(t(cell.bodyKey)) }
    case 'table':
      return {
        id,
        type: 'table',
        caption: cell.captionKey ? t(cell.captionKey) : '',
        rows: (cell.rows ?? []).map((row) => ({
          symbol: row.symbol ?? '',
          value: row.value ?? null,
          unit: row.unit ?? '',
        })),
      }
    case 'plot':
      return {
        id,
        type: 'plot',
        caption: cell.captionKey ? t(cell.captionKey) : '',
        latex: cell.latex ?? '',
        sweep: cell.sweep,
        from: cell.from,
        to: cell.to,
        steps: cell.steps,
      }
    case 'image':
      // An asset belongs to one sheet, so a template can only ever carry an empty
      // image cell: the reader uploads their own picture into it.
      return { id, type: 'image', assetId: null, alt: cell.alt ?? '' }
  }
}

/**
 * A starter becomes a sheet with **no title**. A starter is structure -- "inputs,
 * working, result" is not what the engineer's sheet is *about* -- and a title of
 * "Blank" would be the one thing every user renames first.
 */
export function materialiseStarter(starter: StarterRecipe, t: Translate): SheetDoc {
  return {
    ...emptySheet(),
    title: '',
    cells: starter.cells.map((cell) => materialiseCell(cell, t)),
  }
}

/**
 * A workspace template becomes a sheet **named after it**: a firm's "Purlin check"
 * template yields a "Purlin check" sheet, which is what the colleague expected.
 *
 * Every cell gets a fresh id. Nothing in a document refers to another cell by id --
 * the dependency graph is derived from symbols -- so remapping is safe, and it means
 * two sheets made from one template never share an id.
 */
export function materialiseWorkspaceTemplate(template: WorkspaceTemplateDetail): SheetDoc {
  return {
    ...emptySheet(),
    title: template.title,
    cells: template.document.cells.map((cell) => ({ ...cell, id: newCellId() })),
  }
}
